/**
 * Notification bus — bridges non-React modules (axios api client, etc.)
 * to the React notification system in PlatformContext.
 *
 * PlatformProvider registers a handler on mount via `registerNotificationHandler`.
 * Any module can then call `notify({...})` without importing React.
 *
 * Includes a 2-second dedupe window so a burst of identical errors (e.g. five
 * parallel API calls all returning 403) collapses into a single toast.
 */

let handler = null;

const recentMessages = new Map(); // message -> timestamp
const DEDUPE_WINDOW_MS = 2000;

export function registerNotificationHandler(fn) {
  handler = fn;
  return () => {
    if (handler === fn) handler = null;
  };
}

export function notify(notification) {
  if (!handler) {
    // PlatformProvider hasn't mounted yet (e.g. error during initial auth
    // validation). Drop silently — console output from the api layer is
    // enough debug signal.
    return;
  }

  const message = notification?.message || '';
  const now = Date.now();

  // Drop duplicates within the window.
  const lastSeen = recentMessages.get(message);
  if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
    return;
  }
  recentMessages.set(message, now);

  // Cheap GC so the map can't grow unbounded.
  if (recentMessages.size > 32) {
    for (const [key, ts] of recentMessages) {
      if (now - ts >= DEDUPE_WINDOW_MS) recentMessages.delete(key);
    }
  }

  handler(notification);
}
